import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { PRIORITY_LABELS, PRODUCT_THEME, STATUS_META, STATUS_TRANSITIONS } from '../lib/products';
import { cx, fullDate, relativeTime } from '../lib/format';
import type { Permissions, Status, SuggestionDetail, User } from '../lib/types';
import {
  Avatar,
  CategoryChip,
  CloseIcon,
  ErrorState,
  PriorityMeter,
  ProductChip,
  Spinner,
  StatusChip,
  TrashIcon,
  VisibilityChip,
} from './primitives';
import { useToast } from './Toast';

const ACTION_TEXT: Record<string, string> = {
  created: 'opened this suggestion',
  status_changed: 'changed the status',
  assigned: 'assigned it',
  unassigned: 'removed the assignment',
  visibility_changed: 'changed the visibility',
  updated: 'edited the details',
  commented: 'left a comment',
};

export function SuggestionDrawer({
  id,
  onClose,
  onMutated,
  people,
}: {
  id: string | null;
  onClose: () => void;
  onMutated: () => void;
  people: User[];
}) {
  const toast = useToast();

  const [data, setData] = useState<SuggestionDetail | null>(null);
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [commentDraft, setCommentDraft] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [showResolveNote, setShowResolveNote] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(id, signal);
        if (signal?.aborted) return;
        setData(res.suggestion);
        setPermissions(res.permissions);
      } catch (err) {
        if (signal?.aborted) return;
        setError(
          err instanceof ApiError
            ? err.status === 404
              ? 'This suggestion is not available — it may have been deleted, or made private.'
              : err.message
            : 'Could not load this suggestion.',
        );
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    // This panel is one long-lived component instance — DashboardPage never
    // unmounts it, it just swaps `id` — so nothing here resets itself for
    // free between suggestions. Clear every transient UI flag on every id
    // change (not only on close) as a backstop: a handler that forgets its
    // own cleanup (as the delete button once did — see its comment) should
    // still self-heal the next time a suggestion is opened, rather than
    // staying stuck for the rest of the session.
    setBusyAction(null);
    setCommentDraft('');
    setShowResolveNote(false);
    setResolveNote('');
    setConfirmDelete(false);
    setLightbox(false);

    if (!id) {
      setData(null);
      setPermissions(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [id, load]);

  /* Escape closes the lightbox first, then the drawer. */
  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (lightbox) setLightbox(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [id, onClose, lightbox]);

  useEffect(() => {
    if (data && panelRef.current) panelRef.current.focus();
  }, [data]);

  async function run(label: string, fn: () => Promise<unknown>, successMessage: string) {
    setBusyAction(label);
    try {
      await fn();
      toast(successMessage, 'success');
      await load();
      onMutated();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'That did not work. Please try again.', 'error');
    } finally {
      setBusyAction(null);
    }
  }

  async function changeStatus(next: Status) {
    if (!data) return;
    if (next === 'resolved' && !showResolveNote) {
      // Ask for a resolution note before closing something out.
      setShowResolveNote(true);
      return;
    }
    await run(
      `status-${next}`,
      () => api.setStatus(data.id, next, next === 'resolved' ? resolveNote.trim() || undefined : undefined),
      next === 'resolved' ? 'Marked as resolved.' : `Moved to ${STATUS_META[next].label}.`,
    );
    setShowResolveNote(false);
    setResolveNote('');
  }

  async function postComment(e: React.FormEvent) {
    e.preventDefault();
    if (!data || !commentDraft.trim() || postingComment) return;
    setPostingComment(true);
    try {
      await api.comment(data.id, commentDraft.trim());
      setCommentDraft('');
      await load();
      onMutated();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not post that comment.', 'error');
    } finally {
      setPostingComment(false);
    }
  }

  const theme = data ? PRODUCT_THEME[data.product] : null;

  return (
    <AnimatePresence>
      {id && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-ink-950/75 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.aside
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Suggestion details"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 34 }}
            className="hairline relative flex h-full w-full max-w-2xl flex-col border-l bg-ink-900/95 backdrop-blur-2xl outline-none"
          >
            {/* Product hue wash at the top of the panel. */}
            {theme && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-40 opacity-40"
                style={{ background: `linear-gradient(180deg, ${theme.hex}22, transparent)` }}
              />
            )}

            <header className="hairline relative flex items-center justify-between gap-4 border-b px-6 py-4">
              <h2 className="text-xs font-semibold tracking-widest text-mist-500 uppercase">
                Suggestion detail
              </h2>
              <button type="button" onClick={onClose} className="btn-subtle -mr-2 p-2" aria-label="Close panel">
                <CloseIcon className="h-5 w-5" />
              </button>
            </header>

            <div className="relative flex-1 overflow-y-auto">
              {loading && !data && (
                <div className="space-y-4 p-6">
                  <div className="skeleton h-5 w-40 rounded-full" />
                  <div className="skeleton h-8 w-4/5" />
                  <div className="skeleton h-40 w-full rounded-xl" />
                  <div className="space-y-2">
                    <div className="skeleton h-3 w-full" />
                    <div className="skeleton h-3 w-11/12" />
                    <div className="skeleton h-3 w-3/4" />
                  </div>
                </div>
              )}

              {error && !loading && (
                <div className="p-6">
                  <ErrorState title="Unavailable" message={error} onRetry={() => void load()} />
                </div>
              )}

              {data && (
                <div className="space-y-8 px-6 py-6">
                  {/* Header block */}
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <ProductChip product={data.product} />
                      <CategoryChip category={data.category} />
                      <StatusChip status={data.status} />
                      <VisibilityChip visibility={data.visibility} />
                    </div>

                    <h1 className="text-h1 font-semibold text-mist-50 text-balance">{data.title}</h1>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                      <span className="flex items-center gap-2">
                        <Avatar user={data.submittedBy} size={26} />
                        <span className="text-mist-200">{data.submittedBy.name}</span>
                        {data.submittedBy.title && (
                          <span className="text-mist-600">· {data.submittedBy.title}</span>
                        )}
                      </span>
                      <span className="text-mist-600" title={fullDate(data.createdAt)}>
                        {relativeTime(data.createdAt)}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-xs text-mist-500">Impact</span>
                        <PriorityMeter value={data.priority} />
                        <span className="text-xs text-mist-400">{PRIORITY_LABELS[data.priority]}</span>
                      </span>
                    </div>
                  </div>

                  {data.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setLightbox(true)}
                      className="hairline block w-full overflow-hidden rounded-xl border transition-transform duration-300 hover:scale-[1.005]"
                      aria-label="View attachment full size"
                    >
                      <img src={data.imageUrl} alt={`Attachment for ${data.title}`} className="w-full object-cover" />
                    </button>
                  )}

                  <div className="whitespace-pre-wrap text-[0.9375rem] leading-relaxed text-mist-200 text-pretty">
                    {data.description}
                  </div>

                  {data.resolutionNote && (
                    <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/8 p-4">
                      <p className="mb-1.5 text-xs font-semibold tracking-wide text-emerald-300 uppercase">
                        Resolution
                      </p>
                      <p className="text-sm leading-relaxed text-emerald-100/90 text-pretty">
                        {data.resolutionNote}
                      </p>
                      {data.resolvedAt && (
                        <p className="mt-2 text-xs text-emerald-200/50">{fullDate(data.resolvedAt)}</p>
                      )}
                    </div>
                  )}

                  {/* Assignment + workflow */}
                  <section
                    className="surface space-y-4 p-4"
                    style={{ borderLeft: `3px solid ${theme?.hex}` }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-xs font-semibold tracking-widest text-mist-500 uppercase">
                        Ownership
                      </h3>
                      {data.assignedTo ? (
                        <span className="flex items-center gap-2">
                          <Avatar user={data.assignedTo} size={24} />
                          <span className="text-sm text-mist-200">{data.assignedTo.name}</span>
                        </span>
                      ) : (
                        <span className="text-sm text-mist-600">Unassigned</span>
                      )}
                    </div>

                    {permissions?.canAssign && (
                      <div>
                        <label htmlFor="assignee" className="label">
                          Assign to
                        </label>
                        <select
                          id="assignee"
                          value={data.assignedTo?.id ?? ''}
                          disabled={busyAction === 'assign'}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            void run(
                              'assign',
                              () => api.assign(data.id, value),
                              value ? 'Assignment updated.' : 'Assignment cleared.',
                            );
                          }}
                          className="field"
                        >
                          <option value="">Unassigned</option>
                          {people.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {p.title ? ` — ${p.title}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {permissions?.canManage ? (
                      <div>
                        <span className="label">Move to</span>
                        <div className="flex flex-wrap gap-2">
                          {STATUS_TRANSITIONS[data.status].map((next) => (
                            <button
                              key={next}
                              type="button"
                              disabled={busyAction === `status-${next}`}
                              onClick={() => void changeStatus(next)}
                              className={cx('btn-ghost text-xs', next === 'resolved' && 'border-emerald-400/30 text-emerald-200')}
                            >
                              {busyAction === `status-${next}` ? (
                                <Spinner className="h-3.5 w-3.5" />
                              ) : (
                                <span className={cx('h-1.5 w-1.5 rounded-full', STATUS_META[next].dot)} />
                              )}
                              {STATUS_META[next].label}
                            </button>
                          ))}
                        </div>

                        <AnimatePresence>
                          {showResolveNote && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-3 space-y-2">
                                <label htmlFor="resolve-note" className="label">
                                  Resolution note
                                </label>
                                <textarea
                                  id="resolve-note"
                                  value={resolveNote}
                                  onChange={(e) => setResolveNote(e.target.value)}
                                  rows={3}
                                  placeholder="What was done? Anyone reading this later should understand the outcome."
                                  className="field resize-y"
                                />
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowResolveNote(false);
                                      setResolveNote('');
                                    }}
                                    className="btn-subtle text-xs"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    disabled={busyAction === 'status-resolved'}
                                    onClick={() => void changeStatus('resolved')}
                                    className="btn-primary text-xs"
                                  >
                                    {busyAction === 'status-resolved' ? (
                                      <Spinner className="h-3.5 w-3.5" />
                                    ) : null}
                                    Confirm resolved
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <p className="text-xs leading-relaxed text-mist-600">
                        Only the assignee or an admin can change the status of this suggestion.
                      </p>
                    )}
                  </section>

                  {/* Comments */}
                  <section className="space-y-4">
                    <h3 className="text-xs font-semibold tracking-widest text-mist-500 uppercase">
                      Discussion {data.comments.length > 0 && `(${data.comments.length})`}
                    </h3>

                    {data.comments.length === 0 ? (
                      <p className="hairline rounded-xl border border-dashed px-4 py-6 text-center text-sm text-mist-600">
                        No comments yet. Add the first one.
                      </p>
                    ) : (
                      <ul className="space-y-3">
                        {data.comments.map((c) => (
                          <li key={c.id} className="flex gap-3">
                            <Avatar user={c.author} size={30} />
                            <div className="chip-idle min-w-0 flex-1 rounded-xl border px-4 py-3">
                              <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
                                <span className="text-sm font-medium text-mist-50">{c.author.name}</span>
                                <span className="text-xs text-mist-600" title={fullDate(c.createdAt)}>
                                  {relativeTime(c.createdAt)}
                                </span>
                              </div>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap text-mist-200 text-pretty">
                                {c.body}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    <form onSubmit={postComment} className="space-y-2">
                      <label htmlFor="comment" className="sr-only">
                        Add a comment
                      </label>
                      <textarea
                        id="comment"
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        rows={3}
                        maxLength={2000}
                        placeholder="Add context, ask a question, or share what you found…"
                        className="field resize-y"
                      />
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={!commentDraft.trim() || postingComment}
                          className="btn-primary text-xs"
                        >
                          {postingComment ? <Spinner className="h-3.5 w-3.5" /> : null}
                          Post comment
                        </button>
                      </div>
                    </form>
                  </section>

                  {/* Activity */}
                  <section className="space-y-3">
                    <h3 className="text-xs font-semibold tracking-widest text-mist-500 uppercase">
                      Activity
                    </h3>
                    <ol className="hairline space-y-3 border-l pl-4">
                      {data.activity.map((a) => (
                        <li key={a.id} className="relative">
                          <span
                            aria-hidden="true"
                            className="absolute top-1.5 -left-[1.3125rem] h-2 w-2 rounded-full bg-mist-600 ring-3 ring-ink-900"
                          />
                          <p className="text-sm text-mist-200">
                            <span className="font-medium text-mist-50">{a.actor.name}</span>{' '}
                            {ACTION_TEXT[a.action] ?? a.action}
                            {a.meta && typeof a.meta.to === 'string' && a.action === 'status_changed' && (
                              <>
                                {' '}to{' '}
                                <span className="font-medium text-mist-50">
                                  {STATUS_META[a.meta.to as Status]?.label ?? String(a.meta.to)}
                                </span>
                              </>
                            )}
                          </p>
                          {a.note && (
                            <p className="mt-1 text-xs leading-relaxed text-mist-500 italic">“{a.note}”</p>
                          )}
                          <p className="mt-0.5 text-xs text-mist-600" title={fullDate(a.createdAt)}>
                            {relativeTime(a.createdAt)}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </section>

                  {permissions?.canDelete && (
                    <section className="hairline border-t pt-5">
                      {confirmDelete ? (
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-400/30 bg-rose-500/8 px-4 py-3">
                          <p className="text-sm text-rose-200">
                            Delete this suggestion permanently?
                          </p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(false)}
                              className="btn-subtle text-xs"
                            >
                              Keep it
                            </button>
                            <button
                              type="button"
                              disabled={busyAction === 'delete'}
                              onClick={async () => {
                                setBusyAction('delete');
                                try {
                                  await api.remove(data.id);
                                  toast('Suggestion deleted.', 'success');
                                  onMutated();
                                  onClose();
                                } catch (err) {
                                  toast(
                                    err instanceof ApiError ? err.message : 'Could not delete it.',
                                    'error',
                                  );
                                } finally {
                                  // Always clear this, success or failure: this
                                  // panel never unmounts between suggestions
                                  // (DashboardPage keeps one instance alive and
                                  // just swaps `id`), so leaving busyAction set
                                  // here permanently disabled deleting every
                                  // suggestion opened afterward, for the rest
                                  // of the session.
                                  setBusyAction(null);
                                }
                              }}
                              className="btn text-xs border border-rose-400/40 bg-rose-500/20 text-rose-100 hover:bg-rose-500/30"
                            >
                              {busyAction === 'delete' ? <Spinner className="h-3.5 w-3.5" /> : null}
                              Delete permanently
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(true)}
                          className="btn-subtle text-xs text-rose-300/70 hover:text-rose-200"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          Delete suggestion
                        </button>
                      )}
                    </section>
                  )}
                </div>
              )}
            </div>
          </motion.aside>

          {/* Full-size attachment */}
          <AnimatePresence>
            {lightbox && data?.imageUrl && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-50 flex items-center justify-center bg-ink-950/95 p-6"
                onClick={() => setLightbox(false)}
                role="dialog"
                aria-label="Attachment preview"
              >
                <motion.img
                  initial={{ scale: 0.94 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.94 }}
                  src={data.imageUrl}
                  alt={`Attachment for ${data.title}`}
                  className="max-h-full max-w-full rounded-xl object-contain shadow-lift"
                />
                <button
                  type="button"
                  onClick={() => setLightbox(false)}
                  className="btn-ghost absolute top-6 right-6"
                  aria-label="Close preview"
                >
                  <CloseIcon className="h-5 w-5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </AnimatePresence>
  );
}
